import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider } from '@mui/material/styles';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_VISUAL_OPTION,
  STORAGE_KEY,
  applyVisualOptionVars,
  getVisualOption,
  isVisualOptionId,
} from '../design/visualOptions';
import { createAppTheme } from '../theme';

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

function readStoredVisualOption() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isVisualOptionId(saved)) return saved;
  } catch {
    /* ignore */
  }
  return DEFAULT_VISUAL_OPTION;
}

const ThemeContextProvider = ({ children }) => {
  const getSystemTheme = () => {
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return true;
    }
    return false;
  };

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode === 'true' || savedMode === 'false') return savedMode === 'true';
    } catch { /* storage unavailable */ }
    return getSystemTheme();
  });

  const [visualOptionId, setVisualOptionIdState] = useState(readStoredVisualOption);

  useEffect(() => {
    try { localStorage.setItem('darkMode', JSON.stringify(darkMode)); } catch { /* storage unavailable */ }
  }, [darkMode]);

  useEffect(() => {
    const option = getVisualOption(visualOptionId);
    applyVisualOptionVars(option);
    const root = document.documentElement;
    root.setAttribute('data-canvas-mode', darkMode ? 'dark' : 'light');
    if (darkMode) {
      const dark = option.mui.dark;
      root.style.setProperty('--canvas-bg', dark.backgroundDefault);
      root.style.setProperty('--canvas-bg-elevated', dark.backgroundPaper);
      root.style.setProperty('--canvas-field-bg', dark.backgroundPaper);
      root.style.setProperty('--canvas-text', '#f2f4f8');
      root.style.setProperty('--canvas-muted', '#9aa3b5');
      root.style.setProperty('--canvas-primary', dark.primary);
      root.style.setProperty('--canvas-accent', dark.secondary);
      root.style.setProperty('--canvas-border', 'rgba(220, 230, 245, 0.12)');
      root.style.setProperty(
        '--canvas-border-strong',
        'color-mix(in srgb, ' + dark.primary + ' 45%, transparent)'
      );
      root.style.setProperty(
        '--canvas-primary-soft',
        'color-mix(in srgb, ' + dark.primary + ' 12%, transparent)'
      );
      root.style.setProperty(
        '--canvas-focus-ring',
        'color-mix(in srgb, ' + dark.primary + ' 30%, transparent)'
      );
      root.style.setProperty(
        '--canvas-field-hover',
        'color-mix(in srgb, ' + dark.primary + ' 8%, transparent)'
      );
      root.style.setProperty(
        '--canvas-shell-bg',
        'radial-gradient(800px 480px at 10% -8%, color-mix(in srgb, ' +
          dark.primary +
          ' 14%, transparent), transparent 55%), linear-gradient(165deg, ' +
          dark.backgroundDefault +
          ' 0%, ' +
          dark.backgroundPaper +
          ' 100%)'
      );
      root.style.setProperty('--canvas-surface', dark.backgroundPaper);
      if (option.id === 'studio') root.style.setProperty('--canvas-shell-bg', dark.backgroundDefault);
    }
    try {
      localStorage.setItem(STORAGE_KEY, visualOptionId);
    } catch {
      /* ignore */
    }
  }, [visualOptionId, darkMode]);

  const setVisualOptionId = useCallback((id) => {
    if (isVisualOptionId(id)) {
      setVisualOptionIdState(id);
    }
  }, []);

  const theme = useMemo(
    () => createAppTheme(darkMode ? 'dark' : 'light', visualOptionId),
    [darkMode, visualOptionId]
  );

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const globalStyles = (
    <GlobalStyles
      styles={{
        body: {
          background: 'var(--canvas-shell-bg, var(--canvas-bg))',
          backgroundAttachment: 'fixed',
          color: darkMode ? '#ffffff' : 'var(--canvas-text)',
        },
      }}
    />
  );

  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        toggleDarkMode,
        visualOptionId,
        setVisualOptionId,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
